import SmallSpinner from "@ihs-widget/components/common/SmallSpinner";
import { truncateString } from "@ihs-widget/utils/functions";
import { useLanguage } from "@ihs-widget/utils/hooks/useLanguage";
import { useMemo } from "react";
import { useTranslations } from "../../../../../translation/TranslationContext";
import SmallSpinnerTimeout from "@ihs-widget/components/common/SmallSpinnerTimeout";
import useTimeoutVisability from "@ihs-widget/utils/hooks/useTimeoutVisability";
import { useColorContext } from "@ihs-widget/stores/ColorContext";

interface SingleLineStatisticProps {
    label: string;
    home: number;
    away: number;
    isPercentage: boolean;
    isSmall?: boolean;
    mt?: string;
    mb?: string;
}

const SingleLineStatistic = ({ label, home, away, isPercentage, isSmall = false, mt = 'mt-[24px]', mb = 'mb-0' }: SingleLineStatisticProps) => {
    const language = useLanguage();
    const translations = useTranslations(language);
    const { colors, isDynamicColors } = useColorContext();
    
    const translatedLabel = useMemo(() => {
        const t = translations[label as keyof typeof translations] as unknown as string;
        return typeof t === 'string' ? t : '';
    }, [translations, label]);

    const normalizedHome = useMemo(() => Math.max(0, home), [home]);
    const normalizedAway = useMemo(() => Math.max(0, away), [away]);

    // Timeout logic for hiding component when both values are null for 10 seconds
    const bothValuesNull = home === null && away === null;
    const { isVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [bothValuesNull, home, away]
    });

    const calculateWidths = (home: number | null, away: number | null) => {
        // If both values are null, 50/50
        if (home === null && away === null) return [50, 50];
        
        // If one value is null and the other is 0, the bar is full for the 0 value
        if (home === null && away === 0) return [0, 100];
        if (home === 0 && away === null) return [100, 0];
        
        // If both values are 0, 50/50
        if (home === 0 && away === 0) return [50, 50];
        
        // If one value is null and the other is greater than 0, the bar is full for the value > 0
        if (home === null && away !== null && away > 0) return [0, 100];
        if (away === null && home !== null && home > 0) return [100, 0];
        
        // Standard logic for two numeric values
        const total = (home || 0) + (away || 0);
        if (total === 0) return [50, 50];
        
        const homeWidth = ((home || 0) / total) * 100;
        const awayWidth = ((away || 0) / total) * 100;
        
        return [homeWidth, awayWidth];
    };

    const [homeWidth, awayWidth] = calculateWidths(home, away);

    // Hide component if both values are null for 5 seconds
    if (bothValuesNull && !isVisible) {
        return null;
    }

    return (
        <div 
            data-testid={`${label.toLowerCase().replace(' ', '-')}`} // example: sc_stats_ball_possession
            className={`w-full h-[28px] flex items-center justify-between flex-col ${isSmall ? 'mt-0' : mt ? mt : 'mt-[24px]'} ${mb ? mb : 'mb-0'}`}>
            <div className="w-full h-[24px] flex justify-center items-center font-robotoMedium 
                            font-medium text-base120 dark:text-monoY relative">
                {home !== null ? <div className="absolute left-0 text-[16px] leading-[24px] min-w-[40px]" 
                data-testid="home-value"
                style={{ color: colors?.textColor }}
                >{truncateString(isPercentage ? `${normalizedHome}%` : normalizedHome, 4)}</div> : <div className="absolute left-0"><SmallSpinnerTimeout /></div>}
                
                <div className="text-[12px] leading-[16px] truncate px-[4px]"
                    style={{ color: colors?.textColor }}
                >{translatedLabel}</div> 

                {away !== null ? <div className="absolute right-0 text-[16px] leading-[24px] min-w-[40px] text-right" 
                data-testid="away-value"
                style={{ color: colors?.textColor }}
                >{truncateString(isPercentage ? `${normalizedAway}%` : normalizedAway, 4)}</div> : <div className="absolute right-0"><SmallSpinnerTimeout /></div>}
            </div>
            <div className="w-full h-[4px] flex flex-row gap-[2px]">
                <div className={`h-[4px] ${homeWidth === 100 ? 'rounded-full' : 'rounded-l-full'} ${homeWidth === 0 ? 'hidden' : ''}`} 
                     style={{ width: `${homeWidth}%`, 
                     backgroundColor: colors?.homeColor, 
                     borderColor: isDynamicColors ? colors?.borderColor : colors?.homeColor, 
                     borderWidth: isDynamicColors ? '0.6px' : '0px'}}
                     data-testid="home-bar"></div>
                <div className={`h-[4px] ${awayWidth === 100 ? 'rounded-full' : 'rounded-r-full '} ${awayWidth === 0 ? 'hidden' : ''}`} 
                     style={{ width: `${awayWidth}%`, 
                     backgroundColor: colors?.awayColor, 
                     borderColor: isDynamicColors ? colors?.borderColor : colors?.awayColor, 
                     borderWidth: isDynamicColors ? '0.6px' : '0px'}}
                     data-testid="away-bar"></div>
            </div>
        </div>
    )
}

export default SingleLineStatistic;
